import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import { toast } from 'react-toastify';

import Modal from 'components/Modal';
import Button from 'components/Button';
import FormInput from 'components/FormInput';
import FormSelect from 'components/FormSelect';
import { useAppContext } from 'contexts/AppContext';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';

import './styles.scss';
import { toastStyle } from 'utils/helper';
import {
  buildBudgetTemplateOptions,
  getUserBudgetTemplates,
  isUserTemplateOption,
} from 'utils/budgetTemplates';
import {
  currencyOptionFor,
  currencyOptions,
  normalizeCurrencyCode,
} from 'utils/currencies';

const CreateBudget = () => {
  const { userData, userProfile } = useAppContext();
  const {
    createBudgetMutation,
    duplicateBudgetMutation,
    refetchBudgets,
    budgetList,
    budgetTypes,
    showCreateBudget,
    hideCreateBudgetModal,
    renameBudget,
    renameTab,
    clearBudgetRename,
    refetchBudgetId,
    openCreatedBudget,
  } = useHomeScreenContext();

  const userTemplates = useMemo(
    () => getUserBudgetTemplates(budgetList, userData?.email),
    [budgetList, userData?.email]
  );
  const budgetTypeOptions = useMemo(
    () =>
      buildBudgetTemplateOptions(
        budgetTypes,
        renameBudget?.id ? [] : userTemplates
      ),
    [budgetTypes, renameBudget?.id, userTemplates]
  );
  const defaultCurrency = normalizeCurrencyCode(
    renameBudget?.primary_currency || userProfile?.userprofile?.default_currency
  );
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      name: renameBudget?.name
        ? renameBudget?.name
        : renameBudget?.full_name
        ? renameBudget?.full_name
        : '',
      template_type: renameBudget?.template_type?.toString() || '',
      primary_currency: currencyOptionFor(defaultCurrency),
    },
  });

  const handleCreateBudget = () =>
    handleSubmit(async data => {
      try {
        const selectedTemplate = data?.template_type;
        const primaryCurrency = normalizeCurrencyCode(
          data?.primary_currency?.value || data?.primary_currency,
          defaultCurrency
        );
        const response =
          !renameBudget?.id && isUserTemplateOption(selectedTemplate)
            ? await duplicateBudgetMutation.mutateAsync({
                budget_id: selectedTemplate?.budget_id,
                withValues: true,
                name: data?.name,
                primary_currency: primaryCurrency,
                is_user_template: false,
                copy_folders: false,
              })
            : await createBudgetMutation.mutateAsync({
                ...data,
                template_type: selectedTemplate?.value,
                primary_currency: primaryCurrency,
                id: renameBudget?.id,
              });
        const newBudget = isUserTemplateOption(selectedTemplate)
          ? response?.data?.data
          : response?.data;
        if (renameBudget?.id) {
          newBudget?.id && renameTab(newBudget.id, newBudget);
          refetchBudgets?.();
          refetchBudgetId?.();
          toast.success(
            `Budget "${renameBudget?.name}" renamed to "${newBudget?.name}" successfully`,
            toastStyle
          );
          handleCancel();
          return;
        }
        if (!newBudget?.id) {
          throw new Error('The created budget response did not include an ID.');
        }

        const openingTab = openCreatedBudget(newBudget);
        handleCancel();
        await openingTab;
      } catch (error) {
        console.error('Budget creation/update failed:', error);
        const message =
          error?.response?.data?.template_type?.[0] ||
          error?.response?.data?.name?.[0] ||
          error?.response?.data?.non_field_errors?.[0];
        toast.error(
          message ||
            `An error occurred while ${
              renameBudget?.id ? 'updating' : 'creating'
            } budget. Please try again later`,
          toastStyle
        );
      }
    });

  const handleCancel = () => {
    hideCreateBudgetModal();
    clearBudgetRename();
    reset();
  };

  return (
    <Modal
      onHide={handleCancel}
      show={showCreateBudget}
      size="sm"
      className="create-budget"
      bodyClass="create-budget__body"
      centered={true}
    >
      <h1 className="create-budget__body__header">
        {renameBudget?.id ? 'Edit' : 'Create'} Budget{' '}
        {watch('name') && (
          <span className="budget-name">&ldquo;{watch('name')}&rdquo;</span>
        )}
      </h1>
      <Form
        className="create-budget__body__form"
        onSubmit={handleCreateBudget()}
        aria-busy={isSubmitting}
      >
        <FormInput
          label="Budget Title"
          type="text"
          error={errors?.name?.message}
          placeholder="Please enter a budget title"
          autoFocus
          {...register('name', { required: 'Budget title is required' })}
        />
        <FormSelect
          label="Budget Template"
          placeholder="Please select a Template"
          options={budgetTypeOptions}
          error={errors?.template_type?.message}
          name="template_type"
          rules={{
            required: false,
          }}
          control={control}
        />
        <FormSelect
          label="Primary Currency"
          placeholder="Select a currency"
          options={currencyOptions}
          error={errors?.primary_currency?.message}
          name="primary_currency"
          rules={{
            required: false,
          }}
          control={control}
          isSearchable
        />
        <div className="create-budget__body__action">
          <Button
            label="Cancel"
            type="reset"
            className="create-budget__action__cancel"
            variant="red-outlined"
            onClick={handleCancel}
            isDisabled={isSubmitting}
          />
          <Button
            label={
              isSubmitting
                ? renameBudget?.id
                  ? 'Saving...'
                  : 'Creating...'
                : renameBudget?.id
                ? 'Confirm'
                : 'Create'
            }
            className="create-budget__action__confirm"
            type="submit"
            isDisabled={isSubmitting}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default CreateBudget;
